import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAppointment } from "@/context/AppointmentContext";
import { callHref } from "@/data/site";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openDialog } = useAppointment();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "glass-nav bg-white/85 border-b border-border shadow-sm"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
          <span className="grid place-items-center h-11 w-11 rounded-xl bg-white shadow-md shadow-brand/20 border border-border/60 overflow-hidden group-hover:scale-105 transition-transform">
            <img src="/unirad-logo.png?v=2" alt="UNIRAD Diagnostics logo" className="h-9 w-9 object-contain" />
          </span>
          <span className="leading-none">
            <span className={`block font-heading font-bold text-lg tracking-tight transition-colors ${scrolled ? "text-brand-navy" : "text-white"}`}>UNIRAD</span>
            <span className={`block text-[10px] tracking-[0.25em] font-semibold transition-colors ${scrolled ? "text-brand" : "text-white/80"}`}>DIAGNOSTICS</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                  isActive
                    ? scrolled ? "text-brand" : "text-white"
                    : scrolled ? "text-brand-navy/70 hover:text-brand" : "text-white/80 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-2">
          <a href={callHref} data-testid="navbar-call-btn">
            <Button variant="outline" className={`rounded-full h-10 gap-2 transition-colors ${scrolled ? "border-brand/30 text-brand-navy hover:bg-brand-soft hover:text-brand bg-transparent" : "border-white/30 text-white bg-white/5 hover:bg-white hover:text-brand-navy"}`}>
              <Phone className="h-4 w-4" /> Call Now
            </Button>
          </a>
          <Button
            data-testid="navbar-book-btn"
            onClick={() => openDialog()}
            className="rounded-full bg-booking hover:bg-booking-dark h-10 gap-2 shadow-md shadow-booking/25"
          >
            <CalendarCheck className="h-4 w-4" /> Book Appointment
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden grid place-items-center h-11 w-11 rounded-xl bg-white border border-border text-brand-navy"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden glass-nav bg-white/95 border-b border-border px-5 pb-6 pt-2">
          <div className="flex flex-col">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `py-3 px-2 text-base font-semibold border-b border-border/60 ${
                    isActive ? "text-brand" : "text-brand-navy/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex gap-3 mt-5">
              <a href={callHref} className="flex-1">
                <Button variant="outline" className="w-full rounded-full border-brand/30 gap-2">
                  <Phone className="h-4 w-4" /> Call
                </Button>
              </a>
              <Button onClick={() => openDialog()} className="flex-1 rounded-full bg-booking hover:bg-booking-dark gap-2">
                <CalendarCheck className="h-4 w-4" /> Book
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
